import {
  PlayerActionSourceFailure,
  type GameAction,
  type GamePhase,
  type LaneId,
  type PlayerActionSource,
  type PlayerDecisionContext,
  type PlayerId,
  type PlayerObservationV1,
  type VisibleArmyCardViewV1,
  type VisibleBattlefieldCommanderV1,
  type VisibleCardViewV1,
  type VisibleLaneV1,
  type VisibleLandStageV1,
  type VisibleResourcePoolV1,
} from "@fate-kingdom/engine";
import {
  canonicalStringify,
  createSeededRandom,
  nextRandomInt,
  type SeededRandomState,
} from "@fate-kingdom/shared";

export interface RandomLegalBotOptions {
  readonly playerId: PlayerId;
  readonly seed: string;
}

export interface RandomBotDecision {
  readonly decisionNumber: number;
  readonly playerId: PlayerId;
  readonly turnNumber: number;
  readonly phase: GamePhase;
  readonly observationHash: string;
  readonly legalActionCount: number;
  readonly randomStateBefore: SeededRandomState;
  readonly selectedIndex: number;
  readonly selectedAction: GameAction;
  readonly randomStateAfter: SeededRandomState;
}

export interface RandomLegalBot extends PlayerActionSource {
  readonly seed: string;
  readonly decisions: readonly RandomBotDecision[];
  readonly randomState: SeededRandomState;
}

export interface RandomLegalSelfPlaySeeds {
  readonly matchSeed: string;
  readonly playerOneBotSeed: string;
  readonly playerTwoBotSeed: string;
}

export const PINNED_RANDOM_LEGAL_SELF_PLAY: RandomLegalSelfPlaySeeds = {
  matchSeed: "random-legal-core-battle-018",
  playerOneBotSeed: "random-legal-p1-018",
  playerTwoBotSeed: "random-legal-p2-018",
};

function cloneSerializable<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function formatOrdinal(ordinal: number): string {
  return ordinal.toString().padStart(3, "0");
}

/** Deterministic, auditable seed pairs for bounded Random Legal Bot smoke runs. */
export function createRandomLegalBatchSeedPairs(count = 25): readonly RandomLegalSelfPlaySeeds[] {
  if (!Number.isSafeInteger(count) || count < 1) {
    throw new RangeError("count must be a positive safe integer.");
  }

  return Array.from({ length: count }, (_, index) => {
    const ordinal = formatOrdinal(index + 1);
    return {
      matchSeed: `random-legal-core-battle-${ordinal}`,
      playerOneBotSeed: `random-legal-p1-${ordinal}`,
      playerTwoBotSeed: `random-legal-p2-${ordinal}`,
    };
  });
}

class RandomLegalBotImplementation implements RandomLegalBot {
  readonly playerId: PlayerId;
  readonly seed: string;
  #randomState: SeededRandomState;
  readonly #decisions: RandomBotDecision[] = [];

  constructor(options: RandomLegalBotOptions) {
    this.playerId = options.playerId;
    this.seed = options.seed;
    this.#randomState = createSeededRandom(options.seed);
  }

  get decisions(): readonly RandomBotDecision[] {
    return cloneSerializable(this.#decisions);
  }

  get randomState(): SeededRandomState {
    return cloneSerializable(this.#randomState);
  }

  nextAction(context: Readonly<PlayerDecisionContext>): GameAction | null {
    if (context.playerId !== this.playerId) {
      throw new PlayerActionSourceFailure(
        "WRONG_PLAYER_CONTEXT",
        "Random Legal Bot received a decision context for another player.",
        { botPlayerId: this.playerId, contextPlayerId: context.playerId },
      );
    }
    if (context.legalActions.length === 0) {
      return null;
    }

    const positiveWindowActions = context.legalActions.filter(
      (action) => action.type !== "end-turn",
    );
    const selectableActions =
      positiveWindowActions.length > 0 ? positiveWindowActions : context.legalActions;
    const randomStateBefore = this.#randomState;
    const draw = nextRandomInt(randomStateBefore, selectableActions.length);
    const selectedAction = selectableActions[draw.value];
    if (selectedAction === undefined) {
      throw new Error("Seeded random selection returned an unavailable legal-action index.");
    }

    const selectedActionCopy = cloneSerializable(selectedAction);
    const decision: RandomBotDecision = {
      decisionNumber: this.#decisions.length,
      playerId: this.playerId,
      turnNumber: context.observation.turnNumber,
      phase: context.observation.phase,
      observationHash: context.observationHash,
      legalActionCount: context.legalActions.length,
      randomStateBefore: cloneSerializable(randomStateBefore),
      selectedIndex: draw.value,
      selectedAction: selectedActionCopy,
      randomStateAfter: cloneSerializable(draw.state),
    };
    this.#randomState = draw.state;
    this.#decisions.push(decision);
    return cloneSerializable(selectedActionCopy);
  }
}

/**
 * Creates a deliberately strategy-free QA bot. It samples uniformly from the
 * authoritative supplied legal set and never constructs an action of its own.
 */
export function createRandomLegalBot(options: RandomLegalBotOptions): RandomLegalBot {
  return new RandomLegalBotImplementation(options);
}

export const BASIC_HEURISTIC_POLICY_VERSION = "basic-heuristic-v1" as const;

export type HeuristicActionCategory =
  | "mandatory-supply-removal"
  | "win-match"
  | "win-lane"
  | "attack-damage"
  | "found-land"
  | "develop-land"
  | "deploy-commander"
  | "replace-commander"
  | "deploy-army"
  | "future-supply-repair"
  | "voluntary-disband"
  | "play-event"
  | "end-turn"
  | "advance-phase"
  | "other";

export interface HeuristicScoreReason {
  readonly code: string;
  readonly value: number;
  readonly explanation: string;
}

export interface HeuristicActionEvaluation {
  readonly action: GameAction;
  readonly category: HeuristicActionCategory;
  readonly score: number;
  readonly reasons: readonly HeuristicScoreReason[];
  readonly canonicalActionKey: string;
}

export interface HeuristicBotDecision {
  readonly decisionNumber: number;
  readonly playerId: PlayerId;
  readonly turnNumber: number;
  readonly phase: GamePhase;
  readonly observationHash: string;
  readonly legalActionCount: number;
  readonly policyVersion: typeof BASIC_HEURISTIC_POLICY_VERSION;
  readonly selectedAction: GameAction;
  readonly selectedScore: number;
  readonly selectedReasons: readonly HeuristicScoreReason[];
  /** Sorted best-first evidence for this one supplied legal-action set. */
  readonly evaluations: readonly HeuristicActionEvaluation[];
}

export interface BasicHeuristicBotOptions {
  readonly playerId: PlayerId;
}

export interface BasicHeuristicBot extends PlayerActionSource {
  readonly policyVersion: typeof BASIC_HEURISTIC_POLICY_VERSION;
  readonly decisions: readonly HeuristicBotDecision[];
}

export interface BasicHeuristicWeights {
  readonly immediateMatchWin: number;
  readonly immediateLaneWin: number;
  readonly positiveDamagePerPoint: number;
  readonly resourceShortfallFixed: number;
  readonly resourceCoverage: number;
  readonly attackValue: number;
  readonly guardValue: number;
  readonly capacityValue: number;
  readonly upkeepPenalty: number;
  readonly costPenalty: number;
  readonly endangeredLaneDefence: number;
  readonly unnecessaryDisbandPenalty: number;
  readonly zeroDamageAttackPenalty: number;
}

/** Initial transparent policy values, not a card-balance claim or difficulty setting. */
export const BASIC_HEURISTIC_DEFAULT_WEIGHTS: BasicHeuristicWeights = Object.freeze({
  immediateMatchWin: 1_000_000,
  immediateLaneWin: 100_000,
  positiveDamagePerPoint: 1_000,
  resourceShortfallFixed: 2_000,
  resourceCoverage: 40,
  attackValue: 24,
  guardValue: 20,
  capacityValue: 8,
  upkeepPenalty: 120,
  costPenalty: 5,
  endangeredLaneDefence: 80,
  unnecessaryDisbandPenalty: 1_000,
  zeroDamageAttackPenalty: 250,
});

const HEURISTIC_CATEGORY_PRIORITY: Readonly<Record<HeuristicActionCategory, number>> = {
  "mandatory-supply-removal": 120,
  "win-match": 110,
  "win-lane": 100,
  "attack-damage": 90,
  "found-land": 80,
  "develop-land": 70,
  "deploy-commander": 60,
  "replace-commander": 50,
  "deploy-army": 40,
  "future-supply-repair": 30,
  "voluntary-disband": 20,
  "play-event": 35,
  "end-turn": 10,
  "advance-phase": 10,
  other: 0,
};

const RESOURCE_KEYS = ["food", "production", "trade", "science", "culture"] as const;

function compareCodeUnits(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function scoreReason(code: string, value: number, explanation: string): HeuristicScoreReason {
  return { code, value, explanation };
}

function poolAmount(pool: Readonly<VisibleResourcePoolV1> | null): number {
  if (pool === null) return 0;
  return RESOURCE_KEYS.reduce((total, key) => total + pool[key], 0);
}

function poolShortfall(
  available: Readonly<VisibleResourcePoolV1>,
  required: Readonly<VisibleResourcePoolV1>,
): number {
  return RESOURCE_KEYS.reduce(
    (total, key) => total + Math.max(0, required[key] - available[key]),
    0,
  );
}

function addPool(
  left: Readonly<VisibleResourcePoolV1>,
  right: Readonly<VisibleResourcePoolV1>,
): VisibleResourcePoolV1 {
  return {
    food: left.food + right.food,
    production: left.production + right.production,
    trade: left.trade + right.trade,
    science: left.science + right.science,
    culture: left.culture + right.culture,
  };
}

function zeroPool(): VisibleResourcePoolV1 {
  return { food: 0, production: 0, trade: 0, science: 0, culture: 0 };
}

function totalUpkeep(armies: readonly VisibleArmyCardViewV1[]): VisibleResourcePoolV1 {
  return armies.reduce((total, army) => addPool(total, army.upkeep), zeroPool());
}

function productionAtStage(
  stages: readonly [VisibleLandStageV1, VisibleLandStageV1, VisibleLandStageV1],
  stage: 1 | 2 | 3,
): VisibleResourcePoolV1 {
  if (stage === 1) return stages[0].production;
  if (stage === 2) return stages[1].production;
  return stages[2].production;
}

function currentLandProduction(lane: Pick<VisibleLaneV1, "own">): VisibleResourcePoolV1 {
  const land = lane.own.land;
  if (land === null) return zeroPool();
  const representativeCard = land.cards[0];
  if (representativeCard === undefined) return zeroPool();
  return productionAtStage(representativeCard.stages, land.stage);
}

function laneCombatAttack(
  lane: Pick<VisibleLaneV1, "own" | "opponent">,
  side: "own" | "opponent",
): number {
  const laneSide = lane[side];
  if (laneSide.commander === null) return 0;
  return (
    (laneSide.commander.attack ?? 0) +
    laneSide.armies.reduce((total, army) => total + army.attack, 0) +
    (laneSide.land?.attackBonus ?? 0)
  );
}

function laneCombatGuard(
  lane: Pick<VisibleLaneV1, "own" | "opponent">,
  side: "own" | "opponent",
): number {
  const laneSide = lane[side];
  return (
    (laneSide.commander?.guard ?? 0) +
    laneSide.armies.reduce((total, army) => total + army.guard, 0) +
    (laneSide.land?.guardBonus ?? 0)
  );
}

function laneAttackDamage(
  lane: Pick<VisibleLaneV1, "own" | "opponent">,
  attacker: "own" | "opponent",
): number {
  const defender = attacker === "own" ? "opponent" : "own";
  if (lane[defender].land === null) return 0;
  return Math.max(0, laneCombatAttack(lane, attacker) - laneCombatGuard(lane, defender));
}

function isEndangered(lane: Pick<VisibleLaneV1, "own" | "opponent">): boolean {
  const currentControl = lane.own.land?.currentControl;
  if (currentControl === undefined) return false;
  const threat = laneAttackDamage(lane, "opponent");
  return threat > 0 && currentControl <= threat;
}

function ownLane(observation: Readonly<PlayerObservationV1>, laneId: LaneId): VisibleLaneV1 {
  return observation.lanes[laneId];
}

function handCard(
  observation: Readonly<PlayerObservationV1>,
  cardInstanceId: string,
): VisibleCardViewV1 | undefined {
  return observation.own.hand.find((card) => card.cardInstanceId === cardInstanceId);
}

function armyInLane(
  lane: Pick<VisibleLaneV1, "own">,
  armyCardInstanceId: string,
): VisibleArmyCardViewV1 | undefined {
  return lane.own.armies.find((army) => army.cardInstanceId === armyCardInstanceId);
}

function cardCostPenalty(card: Pick<VisibleCardViewV1, "cost">): number {
  return poolAmount(card.cost);
}

function armyCombatValue(army: Readonly<VisibleArmyCardViewV1>): number {
  return army.attack * 3 + army.guard * 2 + army.armySize;
}

function commanderCombatValue(commander: Readonly<VisibleBattlefieldCommanderV1>): number {
  return (
    (commander.attack ?? 0) * BASIC_HEURISTIC_DEFAULT_WEIGHTS.attackValue +
    (commander.guard ?? 0) * BASIC_HEURISTIC_DEFAULT_WEIGHTS.guardValue +
    (commander.armyCapacity ?? 0) * BASIC_HEURISTIC_DEFAULT_WEIGHTS.capacityValue
  );
}

function visibleHandDemand(observation: Readonly<PlayerObservationV1>): VisibleResourcePoolV1 {
  return observation.own.hand.reduce(
    (total, card) => (card.cost === null ? total : addPool(total, card.cost)),
    zeroPool(),
  );
}

function visibleOwnedProduction(observation: Readonly<PlayerObservationV1>): VisibleResourcePoolV1 {
  return (Object.values(observation.lanes) as readonly VisibleLaneV1[]).reduce(
    (total, lane) => addPool(total, currentLandProduction(lane)),
    zeroPool(),
  );
}

function missingHandCoverage(
  observation: Readonly<PlayerObservationV1>,
  production: Readonly<VisibleResourcePoolV1>,
): number {
  const demand = visibleHandDemand(observation);
  return RESOURCE_KEYS.reduce(
    (total, key) => total + (demand[key] > 0 && production[key] === 0 ? 1 : 0),
    0,
  );
}

function evaluateAttack(
  action: Extract<GameAction, { readonly type: "attack" }>,
  observation: Readonly<PlayerObservationV1>,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  const sourceLane = ownLane(observation, action.laneId);
  const targetLane = ownLane(observation, action.targetLaneId ?? action.laneId);
  const damage = Math.max(
    0,
    laneCombatAttack(sourceLane, "own") - laneCombatGuard(targetLane, "opponent"),
  );
  const targetControl = targetLane.opponent.land?.currentControl ?? 0;
  const category =
    damage > 0 && targetLane.opponent.land !== null && damage >= targetControl
      ? observation.own.victoryBanners >= 1
        ? "win-match"
        : "win-lane"
      : "attack-damage";
  const reasons: HeuristicScoreReason[] = [];
  let score = 0;

  if (action.targetLaneId !== undefined && action.targetLaneId !== action.laneId) {
    reasons.push(
      scoreReason("FLANK_TARGET", 0, "This exact legal action targets a visible unopposed lane."),
    );
  }

  if (category === "win-match") {
    score += BASIC_HEURISTIC_DEFAULT_WEIGHTS.immediateMatchWin;
    reasons.push(
      scoreReason(
        "SECOND_BANNER_LETHAL",
        BASIC_HEURISTIC_DEFAULT_WEIGHTS.immediateMatchWin,
        "Visible lethal damage would claim the second required Victory Banner.",
      ),
    );
  } else if (category === "win-lane") {
    score += BASIC_HEURISTIC_DEFAULT_WEIGHTS.immediateLaneWin;
    reasons.push(
      scoreReason(
        "LANE_CLAIM_AVAILABLE",
        BASIC_HEURISTIC_DEFAULT_WEIGHTS.immediateLaneWin,
        "Visible damage is enough to reduce the opposing Land to zero Control.",
      ),
    );
  } else if (damage > 0) {
    const value = damage * BASIC_HEURISTIC_DEFAULT_WEIGHTS.positiveDamagePerPoint;
    score += value;
    reasons.push(
      scoreReason(
        "POSITIVE_CONTROL_DAMAGE",
        value,
        `Visible Attack exceeds visible Guard by ${damage} Control damage.`,
      ),
    );
  } else {
    score -= BASIC_HEURISTIC_DEFAULT_WEIGHTS.zeroDamageAttackPenalty;
    reasons.push(
      scoreReason(
        "ZERO_DAMAGE_ATTACK",
        -BASIC_HEURISTIC_DEFAULT_WEIGHTS.zeroDamageAttackPenalty,
        "Visible Guard fully blocks this attack, so phase advance is preferred.",
      ),
    );
  }

  if (damage > 0 && targetControl > damage) {
    reasons.push(
      scoreReason("TARGET_CONTROL_REMAINING", targetControl - damage, "The target remains active."),
    );
  }
  return { category, score, reasons };
}

function evaluateFoundLand(
  action: Extract<GameAction, { readonly type: "found-land" }>,
  observation: Readonly<PlayerObservationV1>,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  const card = handCard(observation, action.cardInstanceId);
  if (card?.type !== "land")
    return evaluateOther("found-land card is not visible in the owner's hand.");

  const lane = ownLane(observation, action.laneId);
  const production = card.stages[0].production;
  const currentProduction = visibleOwnedProduction(observation);
  const missingBefore = missingHandCoverage(observation, currentProduction);
  const missingAfter = missingHandCoverage(observation, addPool(currentProduction, production));
  const coverageGain = missingBefore - missingAfter;
  const resourceValue = poolAmount(production);
  const reasons: HeuristicScoreReason[] = [
    scoreReason(
      "FOUND_EMPTY_ACTIVE_LANE",
      120,
      "Establishes a visible empty lane before development.",
    ),
    scoreReason(
      "RESOURCE_COVERAGE_GAIN",
      coverageGain * BASIC_HEURISTIC_DEFAULT_WEIGHTS.resourceCoverage,
      `Stage I production covers ${coverageGain} currently missing visible-hand resource type(s).`,
    ),
  ];
  let score = 120 + coverageGain * BASIC_HEURISTIC_DEFAULT_WEIGHTS.resourceCoverage + resourceValue;
  if (observation.noTerritoryWarning?.threatenedPlayerId === observation.perspectivePlayerId) {
    const value = BASIC_HEURISTIC_DEFAULT_WEIGHTS.immediateMatchWin;
    score += value;
    reasons.push(
      scoreReason(
        "NO_TERRITORY_EMERGENCY",
        value,
        "This legal Land founding prevents the pending No Territory match loss.",
      ),
    );
  }
  if (observation.kingdomProgress !== undefined && !observation.kingdomProgress.own.complete) {
    const populated = observation.kingdomProgress.own.populatedLaneIds.includes(action.laneId);
    if (!populated) {
      score += 160;
      reasons.push(
        scoreReason(
          "KINGDOM_COMPLETION_PROGRESS",
          160,
          "This first Land contributes permanently to the visible Kingdom Complete objective.",
        ),
      );
    }
  }
  if (isEndangered(lane)) {
    score += BASIC_HEURISTIC_DEFAULT_WEIGHTS.endangeredLaneDefence;
    reasons.push(
      scoreReason(
        "ENDANGERED_LANE_DEFENCE",
        BASIC_HEURISTIC_DEFAULT_WEIGHTS.endangeredLaneDefence,
        "Founding opposite a visible immediate threat establishes an active Land.",
      ),
    );
  }
  return { category: "found-land", score, reasons };
}

function evaluateDevelopLand(
  action: Extract<GameAction, { readonly type: "develop-land" }>,
  observation: Readonly<PlayerObservationV1>,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  const lane = ownLane(observation, action.laneId);
  const card = handCard(observation, action.cardInstanceId);
  const land = lane.own.land;
  if (card?.type !== "land" || land === null || land.stage >= 3) {
    return evaluateOther("Development information is not visible for this legal action.");
  }
  const currentProduction = currentLandProduction(lane);
  const nextProduction = productionAtStage(card.stages, land.stage === 1 ? 2 : 3);
  const delta = RESOURCE_KEYS.reduce(
    (total, key) => total + Math.max(0, nextProduction[key] - currentProduction[key]),
    0,
  );
  const requiredUpkeep = totalUpkeep(lane.own.armies);
  const shortfallBefore = poolShortfall(currentProduction, requiredUpkeep);
  const shortfallAfter = poolShortfall(nextProduction, requiredUpkeep);
  const coverageGain =
    missingHandCoverage(observation, currentProduction) -
    missingHandCoverage(observation, nextProduction);
  const repaired = shortfallBefore - shortfallAfter;
  const reasons: HeuristicScoreReason[] = [
    scoreReason(
      "USEFUL_PRODUCTION_DELTA",
      delta,
      `Development adds ${delta} visible resource unit(s).`,
    ),
  ];
  let score =
    delta * BASIC_HEURISTIC_DEFAULT_WEIGHTS.resourceCoverage +
    coverageGain * BASIC_HEURISTIC_DEFAULT_WEIGHTS.resourceCoverage;
  if (repaired > 0) {
    const value = repaired * BASIC_HEURISTIC_DEFAULT_WEIGHTS.resourceShortfallFixed;
    score += value;
    reasons.push(
      scoreReason(
        "LOCAL_SUPPLY_SHORTFALL_REDUCED",
        value,
        `Development reduces projected local Upkeep shortfall by ${repaired}.`,
      ),
    );
  }
  if (isEndangered(lane)) {
    score += BASIC_HEURISTIC_DEFAULT_WEIGHTS.endangeredLaneDefence;
    reasons.push(
      scoreReason(
        "ENDANGERED_LANE_DEVELOPMENT",
        BASIC_HEURISTIC_DEFAULT_WEIGHTS.endangeredLaneDefence,
        "The developed Land is visibly endangered and gains useful local production.",
      ),
    );
  }
  return { category: "develop-land", score, reasons };
}

function evaluateCommanderPlacement(
  action:
    | Extract<GameAction, { readonly type: "deploy-commander" }>
    | Extract<GameAction, { readonly type: "replace-commander" }>,
  observation: Readonly<PlayerObservationV1>,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  const lane = ownLane(observation, action.laneId);
  const card = handCard(observation, action.commanderCardInstanceId);
  if (card?.type !== "commander") {
    return evaluateOther("Commander information is not visible in the owner's hand.");
  }
  const incomingAttack = card.attack ?? 0;
  const incomingGuard = card.guard ?? 0;
  const incomingCapacity = card.armyCapacity ?? 0;
  const incomingValue =
    incomingAttack * BASIC_HEURISTIC_DEFAULT_WEIGHTS.attackValue +
    incomingGuard * BASIC_HEURISTIC_DEFAULT_WEIGHTS.guardValue +
    incomingCapacity * BASIC_HEURISTIC_DEFAULT_WEIGHTS.capacityValue -
    cardCostPenalty(card) * BASIC_HEURISTIC_DEFAULT_WEIGHTS.costPenalty;
  const reasons: HeuristicScoreReason[] = [
    scoreReason(
      "COMMANDER_COMBAT_VALUE",
      incomingValue,
      "Scores visible Attack, Guard, Capacity and cost.",
    ),
  ];

  if (action.type === "replace-commander") {
    const current = lane.own.commander;
    if (current === null)
      return evaluateOther("Replacement target has no visible current Commander.");
    const delta = incomingValue - commanderCombatValue(current);
    let score = delta;
    if (delta <= BASIC_HEURISTIC_DEFAULT_WEIGHTS.attackValue) {
      score -= BASIC_HEURISTIC_DEFAULT_WEIGHTS.unnecessaryDisbandPenalty;
      reasons.push(
        scoreReason(
          "EXPENSIVE_REPLACEMENT",
          -BASIC_HEURISTIC_DEFAULT_WEIGHTS.unnecessaryDisbandPenalty,
          "Replacement is not a material visible improvement over the current Commander.",
        ),
      );
    } else {
      reasons.push(
        scoreReason(
          "MATERIAL_COMMANDER_IMPROVEMENT",
          delta,
          "Replacement materially improves visible lane value.",
        ),
      );
    }
    if (incomingCapacity < lane.own.armies.reduce((total, army) => total + army.armySize, 0)) {
      score -= BASIC_HEURISTIC_DEFAULT_WEIGHTS.unnecessaryDisbandPenalty;
      reasons.push(
        scoreReason(
          "CAPACITY_PRESSURE",
          -BASIC_HEURISTIC_DEFAULT_WEIGHTS.unnecessaryDisbandPenalty,
          "Incoming Capacity leaves no safe margin over the current garrison.",
        ),
      );
    }
    return { category: "replace-commander", score, reasons };
  }

  let score = incomingValue + 100;
  if (lane.own.armies.length > 0) {
    const value = lane.own.armies.length * 100;
    score += value;
    reasons.push(
      scoreReason(
        "COMMANDERLESS_GARRISON_ACTIVATED",
        value,
        "A visible garrison gains Commander-led attack contribution.",
      ),
    );
  }
  if (isEndangered(lane) && incomingGuard > 0) {
    const value = incomingGuard * BASIC_HEURISTIC_DEFAULT_WEIGHTS.endangeredLaneDefence;
    score += value;
    reasons.push(
      scoreReason(
        "ENDANGERED_LANE_DEFENCE",
        value,
        "Visible Commander Guard protects an endangered Land immediately.",
      ),
    );
  }
  const immediateDamage = Math.max(
    0,
    incomingAttack +
      lane.own.armies.reduce((total, army) => total + army.attack, 0) +
      (lane.own.land?.attackBonus ?? 0) -
      laneCombatGuard(lane, "opponent"),
  );
  if (immediateDamage > 0) {
    const value = immediateDamage * BASIC_HEURISTIC_DEFAULT_WEIGHTS.attackValue;
    score += value;
    reasons.push(
      scoreReason(
        "POSITIVE_ATTACK_OPPORTUNITY",
        value,
        "The commanded lane has visible positive future Control damage.",
      ),
    );
  }
  return { category: "deploy-commander", score, reasons };
}

function evaluateArmyDeployment(
  action: Extract<GameAction, { readonly type: "deploy-army" }>,
  observation: Readonly<PlayerObservationV1>,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  const lane = ownLane(observation, action.laneId);
  const card = handCard(observation, action.armyCardInstanceId);
  if (card?.type !== "army")
    return evaluateOther("Army information is not visible in the owner's hand.");

  const currentDamage = laneAttackDamage(lane, "own");
  const projectedDamage = Math.max(0, currentDamage + card.attack);
  const targetControl = lane.opponent.land?.currentControl ?? 0;
  const requiredUpkeep = totalUpkeep([...lane.own.armies, card]);
  const futureShortfall = poolShortfall(currentLandProduction(lane), requiredUpkeep);
  const reasons: HeuristicScoreReason[] = [];
  let score =
    card.attack * BASIC_HEURISTIC_DEFAULT_WEIGHTS.attackValue +
    card.guard * BASIC_HEURISTIC_DEFAULT_WEIGHTS.guardValue +
    card.armySize * BASIC_HEURISTIC_DEFAULT_WEIGHTS.capacityValue -
    cardCostPenalty(card) * BASIC_HEURISTIC_DEFAULT_WEIGHTS.costPenalty;

  if (targetControl > 0 && projectedDamage >= targetControl) {
    const value = BASIC_HEURISTIC_DEFAULT_WEIGHTS.immediateLaneWin / 2;
    score += value;
    reasons.push(
      scoreReason(
        "LANE_CLAIM_AVAILABLE",
        value,
        "This Army creates visible attack sufficient to claim the opposing lane when an attack is legal.",
      ),
    );
  } else if (projectedDamage > currentDamage) {
    const value =
      (projectedDamage - currentDamage) * BASIC_HEURISTIC_DEFAULT_WEIGHTS.positiveDamagePerPoint;
    score += value;
    reasons.push(
      scoreReason(
        "POSITIVE_CONTROL_DAMAGE",
        value,
        "This Army increases visible future positive Control damage.",
      ),
    );
  }
  if (isEndangered(lane) && card.guard > 0) {
    const value = card.guard * BASIC_HEURISTIC_DEFAULT_WEIGHTS.endangeredLaneDefence;
    score += value;
    reasons.push(
      scoreReason(
        "ENDANGERED_LANE_DEFENCE",
        value,
        "This Army adds Guard to a visibly endangered Land.",
      ),
    );
  }
  if (futureShortfall > 0) {
    const value = futureShortfall * BASIC_HEURISTIC_DEFAULT_WEIGHTS.upkeepPenalty;
    score -= value;
    reasons.push(
      scoreReason(
        "FUTURE_UPKEEP_SHORTFALL",
        -value,
        `This legal emergency deployment creates ${futureShortfall} projected local Upkeep shortfall.`,
      ),
    );
  } else {
    reasons.push(
      scoreReason(
        "SUSTAINABLE_DEPLOYMENT",
        20,
        "Visible local production covers projected Upkeep.",
      ),
    );
    score += 20;
  }
  reasons.push(
    scoreReason(
      "ARMY_GARRISON_ADDED",
      card.armySize,
      "Adds one legal Army card to this public formation.",
    ),
  );
  return { category: "deploy-army", score, reasons };
}

function evaluateMandatorySupplyRemoval(
  action: Extract<GameAction, { readonly type: "discard-army-for-upkeep" }>,
  observation: Readonly<PlayerObservationV1>,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  const lane = ownLane(observation, action.laneId);
  const army = armyInLane(lane, action.armyCardInstanceId);
  if (army === undefined)
    return evaluateOther("Mandatory removal Army is not visible in the owned lane.");
  const production = currentLandProduction(lane);
  const before = poolShortfall(production, totalUpkeep(lane.own.armies));
  const remaining = lane.own.armies.filter(
    (candidate) => candidate.cardInstanceId !== army.cardInstanceId,
  );
  const after = poolShortfall(production, totalUpkeep(remaining));
  const repaired = before - after;
  const combatLoss = armyCombatValue(army);
  const burden = poolAmount(army.upkeep);
  const repairedValue = repaired * BASIC_HEURISTIC_DEFAULT_WEIGHTS.resourceShortfallFixed;
  const score =
    10_000 + repairedValue + burden * BASIC_HEURISTIC_DEFAULT_WEIGHTS.upkeepPenalty - combatLoss;
  return {
    category: "mandatory-supply-removal",
    score,
    reasons: [
      scoreReason(
        "LOCAL_SUPPLY_SHORTFALL_REDUCED",
        repairedValue,
        `Discarding this Army reduces visible local Upkeep shortfall by ${repaired}.`,
      ),
      scoreReason(
        "UPKEEP_BURDEN_REMOVED",
        burden * BASIC_HEURISTIC_DEFAULT_WEIGHTS.upkeepPenalty,
        "Higher printed Upkeep is preferred for mandatory removal when legal.",
      ),
      scoreReason(
        "LOWER_COMBAT_LOSS",
        -combatLoss,
        "Preserves stronger visible Army combat value when alternatives exist.",
      ),
    ],
  };
}

function evaluateArmyDisband(
  action: Extract<GameAction, { readonly type: "disband-army" }>,
  observation: Readonly<PlayerObservationV1>,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  const lane = ownLane(observation, action.laneId);
  const army = armyInLane(lane, action.armyCardInstanceId);
  if (army === undefined) return evaluateOther("Disband Army is not visible in the owned lane.");
  const production = currentLandProduction(lane);
  const before = poolShortfall(production, totalUpkeep(lane.own.armies));
  const remaining = lane.own.armies.filter(
    (candidate) => candidate.cardInstanceId !== army.cardInstanceId,
  );
  const after = poolShortfall(production, totalUpkeep(remaining));
  const repaired = before - after;
  const combatLoss = armyCombatValue(army);
  if (repaired > 0) {
    const value = repaired * BASIC_HEURISTIC_DEFAULT_WEIGHTS.resourceShortfallFixed;
    return {
      category: "future-supply-repair",
      score: value - combatLoss,
      reasons: [
        scoreReason(
          "LOCAL_SUPPLY_SHORTFALL_REDUCED",
          value,
          `Voluntary disband reduces visible projected local Upkeep shortfall by ${repaired}.`,
        ),
        scoreReason(
          "COMBAT_VALUE_LOST",
          -combatLoss,
          "The policy keeps this disband rare and costly.",
        ),
      ],
    };
  }
  return {
    category: "voluntary-disband",
    score: -BASIC_HEURISTIC_DEFAULT_WEIGHTS.unnecessaryDisbandPenalty - combatLoss,
    reasons: [
      scoreReason(
        "UNNECESSARY_DISBAND",
        -BASIC_HEURISTIC_DEFAULT_WEIGHTS.unnecessaryDisbandPenalty,
        "No visible local Supply repair justifies losing this Army.",
      ),
      scoreReason("COMBAT_VALUE_LOST", -combatLoss, "The Army has visible combat value."),
    ],
  };
}

function evaluateCommanderDisband(): Omit<
  HeuristicActionEvaluation,
  "action" | "canonicalActionKey"
> {
  return {
    category: "voluntary-disband",
    score: -BASIC_HEURISTIC_DEFAULT_WEIGHTS.unnecessaryDisbandPenalty * 10,
    reasons: [
      scoreReason(
        "COMMANDER_DISBAND_DISCOURAGED",
        -BASIC_HEURISTIC_DEFAULT_WEIGHTS.unnecessaryDisbandPenalty * 10,
        "Atomic replacement exists; voluntarily disbanding a Commander has no supported tactical benefit.",
      ),
    ],
  };
}

function evaluateAdvancePhase(
  observation: Readonly<PlayerObservationV1>,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  return {
    category: "advance-phase",
    score: 0,
    reasons: [
      scoreReason(
        "NO_POSITIVE_OPTIONAL_ACTION",
        0,
        `Advance from ${observation.phase} when all remaining optional actions are non-positive.`,
      ),
    ],
  };
}

function evaluateEndTurn(
  observation: Readonly<PlayerObservationV1>,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  return {
    category: "end-turn",
    score: 0,
    reasons: [
      scoreReason(
        "NO_POSITIVE_OPTIONAL_ACTION",
        0,
        `End turn from ${observation.phase} after higher-value legal actions are exhausted.`,
      ),
    ],
  };
}

function evaluateEvent(
  action: Extract<GameAction, { readonly type: "play-event" }>,
  observation: Readonly<PlayerObservationV1>,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  const card = handCard(observation, action.cardInstanceId);
  if (card?.type !== "event") {
    return evaluateOther("Event information is not visible in the owner's hand.");
  }
  const score = 60 - cardCostPenalty(card) * BASIC_HEURISTIC_DEFAULT_WEIGHTS.costPenalty;
  return {
    category: "play-event",
    score,
    reasons: [
      scoreReason(
        "LEGAL_EVENT_VALUE",
        score,
        "Uses a visible legal Event before ending the action window.",
      ),
    ],
  };
}

function evaluateOther(
  message: string,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  return {
    category: "other",
    score: -1,
    reasons: [scoreReason("UNSUPPORTED_DEFERRED_ACTION", -1, message)],
  };
}

function evaluateAction(
  action: GameAction,
  observation: Readonly<PlayerObservationV1>,
): Omit<HeuristicActionEvaluation, "action" | "canonicalActionKey"> {
  switch (action.type) {
    case "advance-phase":
      return evaluateAdvancePhase(observation);
    case "end-turn":
      return evaluateEndTurn(observation);
    case "play-event":
      return evaluateEvent(action, observation);
    case "attack":
      return evaluateAttack(action, observation);
    case "found-land":
      return evaluateFoundLand(action, observation);
    case "develop-land":
      return evaluateDevelopLand(action, observation);
    case "deploy-commander":
    case "replace-commander":
      return evaluateCommanderPlacement(action, observation);
    case "deploy-army":
      return evaluateArmyDeployment(action, observation);
    case "discard-army-for-upkeep":
      return evaluateMandatorySupplyRemoval(action, observation);
    case "disband-army":
      return evaluateArmyDisband(action, observation);
    case "disband-commander":
      return evaluateCommanderDisband();
    case "deferred":
      return evaluateOther("Deferred rules are deliberately outside the Basic Heuristic policy.");
  }
}

function compareEvaluations(
  left: Readonly<HeuristicActionEvaluation>,
  right: Readonly<HeuristicActionEvaluation>,
): number {
  if (left.score !== right.score) return right.score - left.score;
  const priorityDifference =
    HEURISTIC_CATEGORY_PRIORITY[right.category] - HEURISTIC_CATEGORY_PRIORITY[left.category];
  if (priorityDifference !== 0) return priorityDifference;
  return compareCodeUnits(left.canonicalActionKey, right.canonicalActionKey);
}

/**
 * Scores only the supplied legal action set from one player-safe context. It
 * does not validate, construct, simulate, or apply actions.
 */
export function evaluateBasicHeuristicActions(
  context: Readonly<PlayerDecisionContext>,
): readonly HeuristicActionEvaluation[] {
  return context.legalActions
    .map((action) => {
      const evaluated = evaluateAction(action, context.observation);
      return {
        action: cloneSerializable(action),
        category: evaluated.category,
        score: evaluated.score,
        reasons: cloneSerializable(evaluated.reasons),
        canonicalActionKey: canonicalStringify(action),
      } satisfies HeuristicActionEvaluation;
    })
    .sort(compareEvaluations)
    .map((evaluation) => cloneSerializable(evaluation));
}

class BasicHeuristicBotImplementation implements BasicHeuristicBot {
  readonly playerId: PlayerId;
  readonly policyVersion = BASIC_HEURISTIC_POLICY_VERSION;
  readonly #decisions: HeuristicBotDecision[] = [];

  constructor(options: Readonly<BasicHeuristicBotOptions>) {
    this.playerId = options.playerId;
  }

  get decisions(): readonly HeuristicBotDecision[] {
    return cloneSerializable(this.#decisions);
  }

  nextAction(context: Readonly<PlayerDecisionContext>): GameAction | null {
    if (context.playerId !== this.playerId) {
      throw new PlayerActionSourceFailure(
        "WRONG_PLAYER_CONTEXT",
        "Basic Heuristic Bot received a decision context for another player.",
        { botPlayerId: this.playerId, contextPlayerId: context.playerId },
      );
    }
    if (context.legalActions.length === 0) return null;

    const evaluations = evaluateBasicHeuristicActions(context);
    const selected = evaluations[0];
    if (selected === undefined) return null;
    const selectedAction = cloneSerializable(selected.action);
    const decision: HeuristicBotDecision = {
      decisionNumber: this.#decisions.length,
      playerId: this.playerId,
      turnNumber: context.observation.turnNumber,
      phase: context.observation.phase,
      observationHash: context.observationHash,
      legalActionCount: context.legalActions.length,
      policyVersion: BASIC_HEURISTIC_POLICY_VERSION,
      selectedAction,
      selectedScore: selected.score,
      selectedReasons: cloneSerializable(selected.reasons),
      evaluations: cloneSerializable(evaluations),
    };
    this.#decisions.push(decision);
    return cloneSerializable(selectedAction);
  }
}

/** Creates a deterministic, visible-information, one-decision policy bot. */
export function createBasicHeuristicBot(
  options: Readonly<BasicHeuristicBotOptions>,
): BasicHeuristicBot {
  return new BasicHeuristicBotImplementation(options);
}

export interface HeuristicBotMatchSeeds {
  readonly matchSeed: string;
  readonly playerOneRandomSeed: string;
  readonly playerTwoRandomSeed: string;
}

export const PINNED_HEURISTIC_VERSUS_RANDOM: HeuristicBotMatchSeeds = {
  matchSeed: "random-legal-core-battle-001",
  playerOneRandomSeed: "heuristic-random-p1-001",
  playerTwoRandomSeed: "heuristic-random-p2-001",
};

export const PINNED_RANDOM_VERSUS_HEURISTIC: HeuristicBotMatchSeeds =
  PINNED_HEURISTIC_VERSUS_RANDOM;

export const PINNED_HEURISTIC_SELF_PLAY: HeuristicBotMatchSeeds = {
  matchSeed: "random-legal-core-battle-001",
  playerOneRandomSeed: "unused-heuristic-self-play-p1",
  playerTwoRandomSeed: "unused-heuristic-self-play-p2",
};

/** Deterministic, symmetric match/bot seed pairs for heuristic comparison. */
export function createHeuristicComparisonSeedPairs(count = 12): readonly HeuristicBotMatchSeeds[] {
  if (!Number.isSafeInteger(count) || count < 1) {
    throw new RangeError("count must be a positive safe integer.");
  }

  return Array.from({ length: count }, (_, index) => {
    const ordinal = formatOrdinal(index + 1);
    return {
      matchSeed: `heuristic-comparison-core-battle-${ordinal}`,
      playerOneRandomSeed: `heuristic-comparison-random-p1-${ordinal}`,
      playerTwoRandomSeed: `heuristic-comparison-random-p2-${ordinal}`,
    };
  });
}
